import React from 'react';
import { Box, Tooltip, Typography, LinearProgress } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { MetalCompositionBrief, MetalValueBrief } from '../types';
import {
  getMetalValidationResults,
  getStatusSummary,
  MetalValidationResult,
} from '../utils/statusHelpers';
import { tokens } from '../theme';

interface CompactMetalStatusProps {
  metalValues: MetalValueBrief[];
  materialGradeCompositions: MetalCompositionBrief[];
}

function calculateNormalizedPercentage(result: MetalValidationResult): number {
  if (result.minValue === undefined || result.maxValue === undefined) {
    return 50;
  }
  const range = result.maxValue - result.minValue;
  if (range <= 0) {
    return 50;
  }
  return Math.min(100, Math.max(0, ((result.measuredValue - result.minValue) / range) * 100));
}

function CompactMetalStatus({
  metalValues,
  materialGradeCompositions,
}: CompactMetalStatusProps): React.ReactElement {
  const results = getMetalValidationResults(metalValues, materialGradeCompositions);
  const summary = getStatusSummary(results);

  const allOk = summary.error === 0 && summary.warning === 0 && summary.missing === 0;

  function getSummaryIcon(): React.ReactElement | null {
    if (allOk) {
      return <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />;
    }
    if (summary.error > 0) {
      return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    }
    if (summary.warning > 0 || summary.missing > 0) {
      return <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
    }
    return null;
  }

  const tooltipContent = (
    <Box sx={{ p: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
        Metallwerte Analyse
      </Typography>
      {results.map((metal, index) => (
        <Box key={index} sx={{ mb: 0.5 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            <Typography variant="caption" sx={{ minWidth: 60 }}>
              {metal.name}:
            </Typography>
            <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>
              {metal.measuredValue.toFixed(2)}%
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 70 }}>
              ({metal.minValue?.toFixed(1)}-{metal.maxValue?.toFixed(1)}%)
            </Typography>
            {metal.status === 'ok' && <CheckIcon sx={{ fontSize: 12, color: 'success.main' }} />}
            {metal.status === 'error' && <ErrorIcon sx={{ fontSize: 12, color: 'error.main' }} />}
            {(metal.status === 'warning' || metal.status === 'missing') && (
              <WarningIcon sx={{ fontSize: 12, color: 'warning.main' }} />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

  const problemResults = results.filter((r) => r.status !== 'ok');

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          p: 0.5,
          borderRadius: 1,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        {getSummaryIcon()}

        <Typography variant="caption" color="text.secondary">
          {allOk ? (
            <span style={{ color: tokens.ok }}>Alle im Bereich</span>
          ) : (
            <>
              {summary.error > 0 && (
                <span style={{ color: tokens.bad }}>{summary.error} außerhalb</span>
              )}
              {summary.error > 0 && summary.missing > 0 && ' / '}
              {summary.missing > 0 && (
                <span style={{ color: tokens.ruby }}>{summary.missing} fehlend</span>
              )}
            </>
          )}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {problemResults.slice(0, 3).map((metal, index) => (
            <Box key={index} sx={{ width: 20 }}>
              <LinearProgress
                variant="determinate"
                value={calculateNormalizedPercentage(metal)}
                sx={{
                  height: 3,
                  borderRadius: 1,
                  bgcolor: 'grey.300',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: metal.status === 'error' ? 'error.main' : 'warning.main',
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Tooltip>
  );
}

export default CompactMetalStatus;
